import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "renameInput", "renameButton", "renameError", "renameCurrentPath",
    "deleteButton", "deleteError", "deleteFolderPath", "deleteActionInput",
    "contextMenu"
  ]
  static values = {
    foldersUrl: String
  }

  connect() {
    this.currentFolderPath = null

    // Close context menu when clicking outside
    document.addEventListener("click", this.closeContextMenu.bind(this))
  }

  disconnect() {
    document.removeEventListener("click", this.closeContextMenu.bind(this))
  }

  // Rename Folder Modal
  showRenameModal(event) {
    event?.preventDefault()

    if (!this.currentFolderPath) return

    this.renameCurrentPathTarget.textContent = this.currentFolderPath
    this.renameInputTarget.value = this.currentFolderPath
    this.hideError(this.renameErrorTarget)
    this.closeContextMenu()

    document.getElementById("rename-folder-modal").showModal()
  }

  async renameFolder(event) {
    event.preventDefault()

    const newPath = this.renameInputTarget.value.trim()
    if (!newPath || !this.currentFolderPath) return

    this.setLoading(this.renameButtonTarget, true)
    this.hideError(this.renameErrorTarget)

    try {
      const response = await fetch(`${this.foldersUrlValue}/${encodeURIComponent(this.currentFolderPath)}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ new_path: newPath })
      })

      const data = await response.json()

      if (response.ok) {
        document.getElementById("rename-folder-modal").close()
        this.showSuccess(data.message || "Folder renamed successfully")
        this.refreshFolderTree()
      } else {
        this.showError(this.renameErrorTarget, data.error || "Failed to rename folder")
      }
    } catch (error) {
      console.error("Error renaming folder:", error)
      this.showError(this.renameErrorTarget, "Failed to rename folder")
    } finally {
      this.setLoading(this.renameButtonTarget, false)
    }
  }

  // Delete Folder Modal
  showDeleteModal(event) {
    event?.preventDefault()

    if (!this.currentFolderPath) return

    this.deleteFolderPathTarget.textContent = this.currentFolderPath
    this.hideError(this.deleteErrorTarget)
    this.closeContextMenu()

    // Reset to "orphan" action
    this.deleteActionInputTargets.forEach(input => {
      input.checked = input.value === "orphan"
    })

    document.getElementById("delete-folder-modal").showModal()
  }

  async deleteFolder(event) {
    event?.preventDefault()

    if (!this.currentFolderPath) return

    const actionType = this.deleteActionInputTargets.find(input => input.checked)?.value || "orphan"

    this.setLoading(this.deleteButtonTarget, true)
    this.hideError(this.deleteErrorTarget)

    try {
      const response = await fetch(`${this.foldersUrlValue}/${encodeURIComponent(this.currentFolderPath)}?action_type=${actionType}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": this.csrfToken
        }
      })

      const data = await response.json()

      if (response.ok) {
        document.getElementById("delete-folder-modal").close()
        this.showSuccess(data.message || "Folder deleted successfully")
        this.refreshFolderTree()

        // If currently viewing this folder, redirect to all media
        if (window.location.search.includes(`folder=${this.currentFolderPath}`)) {
          window.location.href = window.location.pathname
        }
      } else {
        this.showError(this.deleteErrorTarget, data.error || "Failed to delete folder")
      }
    } catch (error) {
      console.error("Error deleting folder:", error)
      this.showError(this.deleteErrorTarget, "Failed to delete folder")
    } finally {
      this.setLoading(this.deleteButtonTarget, false)
    }
  }

  // Context Menu
  showContextMenu(event) {
    const detail = event.detail
    this.currentFolderPath = detail.path

    const contextMenu = this.contextMenuTarget
    contextMenu.style.left = `${detail.x}px`
    contextMenu.style.top = `${detail.y}px`
    contextMenu.classList.remove("hidden")

    event.stopPropagation()
  }

  closeContextMenu(event) {
    if (this.hasContextMenuTarget) {
      this.contextMenuTarget.classList.add("hidden")
    }
  }

  // Helper Methods
  refreshFolderTree() {
    // Find folder tree controller and call refresh
    const folderTreeElement = document.querySelector("[data-controller~='folder-tree']")
    if (folderTreeElement) {
      const folderTreeController = this.application.getControllerForElementAndIdentifier(folderTreeElement, "folder-tree")
      if (folderTreeController) {
        folderTreeController.refresh()
      }
    }
  }

  showSuccess(message) {
    // You can implement a toast notification here
    // For now, just show an alert
    alert(message)
  }

  showError(errorTarget, message) {
    errorTarget.classList.remove("hidden")
    errorTarget.querySelector("p").textContent = message
  }

  hideError(errorTarget) {
    errorTarget.classList.add("hidden")
    errorTarget.querySelector("p").textContent = ""
  }

  setLoading(button, loading) {
    if (loading) {
      button.disabled = true
      button.innerHTML = '<i class="fas fa-spinner fa-spin mr-2"></i>Loading...'
    } else {
      button.disabled = false
      // Restore original text based on button type
      if (button.dataset.folderModalTarget === "renameButton") {
        button.textContent = "Rename Folder"
      } else if (button.dataset.folderModalTarget === "deleteButton") {
        button.textContent = "Delete Folder"
      }
    }
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
